import { useBearer, useProjectId, useServerContext } from '@healthblocks-io/core'
import axios, { AxiosHeaders } from 'axios'
import { buildWebStorage, setupCache } from 'axios-cache-interceptor'
import { useMemo } from 'react'

import { environment } from '@/config/environment'

const fromLocalStorage = buildWebStorage(localStorage, 'axios-cache:')

function useIntegrationAxios(
  integrationSlug: string = environment.healthblocks.integrationSlug, // TODO: Phase the default out in favor of `useIntegration`
) {
  const server = useServerContext()
  const projectId = useProjectId()
  const bearer = useBearer()

  return useMemo(() => {
    const instance = setupCache(
      axios.create({
        baseURL: `${server.api}/integration/${integrationSlug}/endpoint`,
      }),

      // Cache configuration
      {
        ttl: 15 * 60 * 1000, // Default 15 minutes
        interpretHeader: false,
        methods: ['get'],
        cachePredicate: {
          statusCheck: (status) => status >= 200 && status < 400,
        },
        storage: fromLocalStorage,
      },
    )

    instance.interceptors.request.use(
      async (config) => {
        config.params = {
          ...config.params,
          projectId,
        }

        const bearerHeaders = await bearer()
        if (bearerHeaders) {
          if (!config.headers || typeof config.headers.set !== 'function') {
            config.headers = new AxiosHeaders(config.headers)
          }
          Object.entries(bearerHeaders).forEach(([key, value]) => {
            if (value !== undefined) {
              config.headers.set(key, value)
            }
          })
        }

        if (config.responseType === 'blob') {
          config.cache = false
        }

        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )

    return instance
  }, [projectId, bearer, integrationSlug])
}

export { useIntegrationAxios }
